<template>
  <aside
    :class="[
      sidebar.open || mobile ? 'w-[220px]' : 'w-[72px]',
      'bg-slate-800 transition-all duration-300 overflow-hidden sideBar h-full',
    ]"
  >
    <div class="w-full h-full flex-col flex overflow-hidden">
      <div v-if="!mobile" class="w-full flex justify-end">
        <q-icon
          name="o_keyboard_double_arrow_right"
          class="text-white opacity-30 cursor-pointer hover:opacity-50 transition-all duration-300"
          :class="sidebar.open ? 'rotate-180' : ''"
          size="20px"
          @click="sidebar.open = !sidebar.open"
        />
      </div>
      <div
        v-if="sidebar.open"
        class="pl-6 inline-flex w-full items-center"
        :class="mobile ? 'mt-6' : 'mt-1'"
      >
        <div class="font-bold !text-primary text-xl">
          <img
            class="object-cover w-[147px] h-6 mb-1"
            src="@/assets/Meridia.svg"
            alt="Meridia Logo"
          />
          Verify
        </div>
      </div>
      <div v-else class="mt-1 inline-flex w-full justify-center items-center">
        <img class="h-6 ml-6 mr-auto" src="@/assets/Meridia_logo.svg" />
      </div>
      <div
        id="sidebar-navigation"
        class="flex flex-col flex-1 w-full overflow-hidden"
        :class="sidebar.open ? 'mt-10' : 'mt-[72px]'"
      >
        <div
          class="flex-1 overflow-y-auto overflow-x-hidden"
          :class="sidebar.open ? 'px-4' : 'px-5'"
        >
          <div ref="mainNavigationRef">
            <SidebarNavigationGroup
              :mobile="mobile"
              :navigationItemsGroup="
                navigationItemsGroup.filter(
                  (group) => group.position === 'normal'
                )
              "
            />
          </div>
        </div>
        <div id="user-navigation" class="w-full h-min">
          <hr class="pb-4 text-slate-500 opacity-15" />
          <div :class="sidebar.open ? 'px-4' : 'px-5'">
            <div>
              <div
                class="w-full inline-flex items-stretch h-full mb-4"
                :class="[sidebar.open ? '' : 'flex-col']"
              >
                <div
                  class="flex items-center"
                  :class="sidebar.open ? 'mr-sm' : ''"
                >
                  <span class="initials">
                    {{ getInitials }}
                  </span>
                </div>
                <div v-if="sidebar.open" class="flex-1 text-start">
                  <div class="text-slate-500 font-medium text-small">
                    {{ getUserCompany }}
                  </div>
                  <div class="max-w-[130px]">
                    <TooltipTruncate
                      :message="getFullUserName"
                      class="!font-bold !text-slate-200 !text-medium"
                    />
                  </div>
                  <div
                    class="font-medium text-slate-200 text-small whitespace-nowrap overflow-hidden"
                  >
                    {{ userStore.getUserRole(userStore.user) }}
                  </div>
                </div>
                <div
                  v-if="isScrolling"
                  class="cursor-pointer flex justify-center"
                  :class="sidebar.open ? 'pl-2' : 'mt-1'"
                  @click="userBarOpen = !userBarOpen"
                >
                  <q-icon
                    name="o_keyboard_arrow_up"
                    size="20px"
                    class="text-primary hover:brightness-200 transition-all h-full duration-300"
                    :class="userBarOpen ? 'rotate-180' : ''"
                  />
                </div>
              </div>
            </div>
            <div
              :style="userBarStyle"
              class="transition-all duration-300"
              @vue:mounted="calculateUserBarStyle"
            >
              <div
                v-if="
                  navigationItemsGroup
                    .filter((group) => group.position === 'bottom')
                    .find((group) =>
                      group.items.find((item) =>
                        'route' in item
                          ? appStore.isRouteAuthorized(item.route.name)
                          : true
                      )
                    )
                "
              >
                <SidebarNavigationGroup
                  :mobile="mobile"
                  :badge="
                    isNewAvailable ? $t('app.sidebar.new_badge') : undefined
                  "
                  :navigationItemsGroup="
                    navigationItemsGroup.filter(
                      (group) => group.position === 'bottom'
                    )
                  "
                />
              </div>
              <router-link
                v-if="showSettingsLink"
                to="/settings"
                class="text-primary group transition-all text-small rounded-md w-full inline-flex items-center cursor-pointer mt-2"
                :class="[!sidebar.open ? 'justify-center' : 'justify-start']"
              >
                <div class="flex justify-center">
                  <MeridiaIcon
                    name="settings"
                    type="material-icons-outlined"
                    class="text-[20px]"
                    :class="[!sidebar.open ? '' : 'pr-2']"
                  />
                </div>
                <Transition name="fade" :duration="open ? 150 : 0">
                  <div
                    v-if="open"
                    class="justify-center flex font-semibold group-hover:underline"
                  >
                    {{ $t('settings.title') }}
                  </div>
                </Transition>
              </router-link>
              <div
                class="text-primary group transition-all text-small rounded-md w-full inline-flex items-center cursor-pointer mb-4 mt-2"
                :class="[!sidebar.open ? 'justify-center' : 'justify-start']"
                @click="() => userStore.signOut()"
              >
                <div class="flex justify-center">
                  <MeridiaIcon
                    name="logout"
                    type="material-icons-outlined"
                    class="text-[20px]"
                    :class="[!sidebar.open ? '' : 'pr-2']"
                  />
                </div>
                <Transition name="fade" :duration="open ? 150 : 0">
                  <div
                    v-if="open"
                    class="justify-center flex font-semibold group-hover:underline"
                  >
                    {{ $t('app.sidebar.log_out') }}
                  </div>
                </Transition>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useAppStore } from 'src/core/stores/app';
import { useTestProfilesStore } from 'src/modules/dataset/stores/testProfile';
import SidebarNavigationGroup from 'src/core/components/layouts/sidebar/SidebarNavigationGroup.vue';
import { useSidebar } from 'src/core/composables/layouts/sidebar/useSidebar';
import { useUserStore } from 'src/core/stores/user';
import TooltipTruncate from 'src/core/components/Input/TooltipTruncate.vue';
import MeridiaIcon from 'src/design_system/components/icon/MeridiaIcon.vue';

const { mobile } = defineProps<{
  mobile: boolean;
}>();

const appStore = useAppStore();
const userStore = useUserStore();

// Show settings link only if MFA is optional or mandatory for the customer
const showSettingsLink = computed(() => {
  const customer = userStore.user?.customer || userStore.user?.supplier?.customer;
  const mfaConfig = customer?.mfa_configuration?.toUpperCase();
  return mfaConfig === 'OPTIONAL' || mfaConfig === 'MANDATORY';
});

const { getFullUserName, getInitials, getUserCompany } = userStore;
const testProfilesStore = useTestProfilesStore();
const { isNewAvailable } = storeToRefs(testProfilesStore);
const { navigationItemsGroup } = storeToRefs(appStore);
const { sidebar } = useSidebar();

const userBarOpen = ref(true);
const userBarStyle = ref('');
const open = ref(sidebar.value.open);

watch(
  () => userBarOpen.value,
  () => {
    calculateUserBarStyle();
  }
);

watch(sidebar.value, () => {
  if (sidebar.value.open) {
    setTimeout(() => (open.value = true), 150);
  } else {
    open.value = false;
  }
});

onBeforeMount(async () => {
  await testProfilesStore.getTestsInfo();
});

const isScrolling = ref(false);
let userNavigationHeight = 0;
const mainNavigationRef = ref<HTMLElement | null>(null);

/**
 * Lifecycle hook that runs when the component is mounted.
 * - Sets `userNavigationHeight` based on the height of the 'user-navigation' element.
 * - Calls `resizeHandler` to handle initial resize logic.
 * - Adds a 'resize' event listener to the window to call `resizeHandler` on window resize.
 */
onMounted(() => {
  userNavigationHeight =
    window.document.getElementById('user-navigation')?.clientHeight || 0;
  resizeHandler();
  window.addEventListener('resize', resizeHandler);
});

/**
 * Lifecycle hook that runs before the component is unmounted.
 * - Removes the 'resize' event listener from the window.
 */
onBeforeUnmount(() => {
  window.removeEventListener('resize', resizeHandler);
});

/**
 * Calculates and sets the style for the user bar based on its open state and the authorized navigation items.
 *
 * - If the user bar is open, it filters the navigation items groups to include only those positioned at the bottom
 *   and containing at least one authorized item. It then calculates the maximum height of the user bar based on the
 *   number of authorized items.
 * - If the user bar is closed, it sets the maximum height to 0.
 *
 * @returns {void}
 */
function calculateUserBarStyle() {
  if (userBarOpen.value) {
    const groups = navigationItemsGroup.value.filter(
      (group) =>
        group.position === 'bottom' &&
        group.items.some((item) =>
          'route' in item ? appStore.isRouteAuthorized(item.route.name) : true
        )
    );
    const items = groups
      .flatMap((group) => group.items)
      .filter((item) =>
        'route' in item ? appStore.isRouteAuthorized(item.route.name) : true
      );
    // Base height for logout + extra space, plus 30px per nav item, plus 30px for settings if visible
    const settingsHeight = showSettingsLink.value ? 30 : 0;
    userBarStyle.value = `max-height: ${items.length * 30 + 32 + settingsHeight + 60}px`;
  } else {
    userBarStyle.value = 'max-height: 0';
  }
}

/**
 * Handles the resize event to adjust the sidebar's visibility based on the window's width.
 */
function resizeHandler() {
  calculateUserBarShow();
  if (window.innerWidth < 1279) {
    sidebar.value.open = false;
  } else {
    sidebar.value.open = true;
  }
}

/**
 * Calculates whether the user bar should be shown based on the height of the sidebar navigation
 * and the main navigation elements. It updates the `isScrolling` and `userBarOpen` reactive values
 * accordingly.
 */
function calculateUserBarShow() {
  const sidebarNavigationHeight =
    window.document.getElementById('sidebar-navigation')?.clientHeight || 0;
  if (mainNavigationRef.value) {
    const mainNavigationHeight =
      mainNavigationRef.value.scrollHeight >
      mainNavigationRef.value.clientHeight
        ? mainNavigationRef.value.scrollHeight
        : mainNavigationRef.value.clientHeight;
    if (sidebarNavigationHeight < mainNavigationHeight + userNavigationHeight) {
      isScrolling.value = true;
      userBarOpen.value = false;
    } else {
      isScrolling.value = false;
      userBarOpen.value = true;
    }
  }
}
</script>

<style lang="scss">
.closedSidebarExpansionItem {
  .q-item__section--side {
    @apply p-0 #{!important};
  }
  .q-expansion-item__toggle-icon {
    @apply text-lg #{!important};
  }
  .q-item__section--main {
    @apply hidden #{!important};
  }
}

.fadeDelay-enter-from,
.fadeDelay-leave-to {
  opacity: 0;
}
.fadeDelay-enter-active {
  transition: opacity 150ms ease-out;
  transition-delay: 0.2s;
}
.fadeDelay-leave-active {
  transition: opacity 150ms ease-out;
}

.initials {
  @apply block w-8 h-8 rounded-full bg-slate-700 text-medium text-slate-500 font-semibold text-center leading-8;
}

.slide-enter-active,
.slide-leave-active {
  transition: all 10s ease-in-out;
}
.slide-enter-from,
.slide-leave-to {
  overflow: hidden;
  max-height: 0;
  height: 0;
}
.slide-enter-to,
.slide-leave-from {
  overflow: hidden;
  max-height: 100%;
  height: auto;
}
</style>
